import { getScheduleMatchRound } from '../teamSpiritPageUtils'

const normalizeSimulationResult = simulationResult => {
  const values = [
    simulationResult?.winPercent,
    simulationResult?.drawPercent,
    simulationResult?.lossPercent,
    simulationResult?.avgGoalsFor,
    simulationResult?.avgGoalsAgainst,
  ].map(Number)

  if (!values.every(Number.isFinite)) return null

  return {
    winPercent: values[0],
    drawPercent: values[1],
    lossPercent: values[2],
    avgGoalsFor: values[3],
    avgGoalsAgainst: values[4],
    chanceBreakdown: simulationResult.chanceBreakdown || null,
  }
}

export const normalizeGroupMatchState = (match, normalizeAttitude, toDateKey) => ({
  matchId: Number(match?.matchId) || null,
  matchDate: match?.matchDate || null,
  matchDateKey: match?.matchDateKey || toDateKey(match?.matchDate),
  matchRound: Number(match?.matchRound) || null,
  group: Number(match?.group) || null,
  status: Number(match?.status) || null,
  homeTeamId: Number(match?.homeTeamId) || null,
  homeTeamName: String(match?.homeTeamName || '').trim(),
  homeShortTeamName: String(match?.homeShortTeamName || '').trim(),
  awayTeamId: Number(match?.awayTeamId) || null,
  awayTeamName: String(match?.awayTeamName || '').trim(),
  awayShortTeamName: String(match?.awayShortTeamName || '').trim(),
  attitude: normalizeAttitude(match?.attitude),
  hasManualAttitude: Boolean(match?.hasManualAttitude),
  hasManualSpiritBefore: Boolean(match?.hasManualSpiritBefore),
  midfieldInput: String(match?.midfieldInput ?? ''),
  formation: String(match?.formation ?? ''),
  tactic: String(match?.tactic ?? ''),
  simulationResult: normalizeSimulationResult(match?.simulationResult),
  hattrickTeamSpirit:
    match?.hattrickTeamSpirit === null ||
    match?.hattrickTeamSpirit === undefined ||
    match?.hattrickTeamSpirit === ''
      ? null
      : Number(match.hattrickTeamSpirit),
  currentHattrickTeamSpirit:
    match?.currentHattrickTeamSpirit === null ||
    match?.currentHattrickTeamSpirit === undefined ||
    match?.currentHattrickTeamSpirit === ''
      ? null
      : Number(match.currentHattrickTeamSpirit),
  currentHattrickTeamSpiritDate: String(match?.currentHattrickTeamSpiritDate || ''),
  hattrickSpiritBeforeInput:
    match?.hattrickSpiritBeforeInput === null ||
    match?.hattrickSpiritBeforeInput === undefined ||
    match?.hattrickSpiritBeforeInput === ''
      ? null
      : Number(match.hattrickSpiritBeforeInput),
  manualSpiritBeforeInput: String(match?.manualSpiritBeforeInput ?? ''),
  midfieldAnchor: Boolean(match?.midfieldAnchor),
})

export const getOpponentFromMatch = (match, teamId) => {
  const normalizedTeamId = Number(teamId)
  const isHome = Number(match?.homeTeamId) === normalizedTeamId
  const isAway = Number(match?.awayTeamId) === normalizedTeamId
  if (!isHome && !isAway) return { teamId: null, name: '' }

  return isHome
    ? {
        teamId: Number(match?.awayTeamId) || null,
        name: String(match?.awayShortTeamName || match?.awayTeamName || '').trim(),
      }
    : {
        teamId: Number(match?.homeTeamId) || null,
        name: String(match?.homeShortTeamName || match?.homeTeamName || '').trim(),
      }
}

export const buildMatchRowsFromGroup = ({
  group,
  nationalTeamId,
  createInitialMatchRows,
  normalizeGroupMatchState,
  toDateKey,
}) => {
  const selectedTeams = Array.isArray(group?.teams) ? group.teams : []
  const selectedCountries = selectedTeams
    .filter(team => Number(team?.teamId) !== Number(nationalTeamId))
    .map(team => String(team?.name || '').trim())
    .filter(Boolean)
  const rows = createInitialMatchRows(selectedCountries)
  const rowsByMatchRound = new Map(
    rows
      .map(row => [getScheduleMatchRound(row.schedule?.label, group?.tournamentKind), row])
      .filter(([round]) => Number.isFinite(round))
  )
  rows.forEach(row => {
    row.matchRound = getScheduleMatchRound(
      row.schedule?.label,
      group?.tournamentKind
    )
  })
  selectedTeams.forEach(team => {
    const country = String(team?.name || '').trim()
    if (!country) return
    const teamMatches = Array.isArray(team?.matches) ? team.matches : []
    const currentHattrickTeamSpirit = team?.currentHattrickTeamSpirit
    const currentHattrickTeamSpiritDate = team?.currentHattrickTeamSpiritDate
    teamMatches.forEach(match => {
      const row = rowsByMatchRound.get(Number(match?.matchRound))
      if (!row) return
      const normalizedMatch = {
        ...normalizeGroupMatchState(match),
        currentHattrickTeamSpirit,
        currentHattrickTeamSpiritDate,
      }
      if (Number(team?.teamId) === Number(nationalTeamId)) {
        Object.assign(row, {
          matchId: normalizedMatch.matchId,
          attitude: normalizedMatch.attitude,
          hasManualAttitude: normalizedMatch.hasManualAttitude,
          hasManualSpiritBefore: normalizedMatch.hasManualSpiritBefore,
          midfieldInput: normalizedMatch.midfieldInput,
          formation: normalizedMatch.formation,
          tactic: normalizedMatch.tactic,
          simulationResult: normalizedMatch.simulationResult,
          hattrickTeamSpirit: normalizedMatch.hattrickTeamSpirit,
          currentHattrickTeamSpirit: normalizedMatch.currentHattrickTeamSpirit,
          currentHattrickTeamSpiritDate: normalizedMatch.currentHattrickTeamSpiritDate,
          hattrickSpiritBeforeInput: normalizedMatch.hattrickSpiritBeforeInput,
          manualSpiritBeforeInput: normalizedMatch.manualSpiritBeforeInput,
          midfieldAnchor: normalizedMatch.midfieldAnchor,
        })
      } else {
        row.countryValues = { ...(row.countryValues || {}), [country]: normalizedMatch }
      }
    })
  })
  return rows
}

export const buildGroupPayloadFromMatchRows = ({
  group,
  matchRows,
  nationalTeamId,
  normalizeAttitude,
  toDateKey,
}) => {
  if (!group || !Array.isArray(group.teams)) return group
  const rowsByMatchRound = new Map(
    (Array.isArray(matchRows) ? matchRows : [])
      .map(row => [getScheduleMatchRound(row.schedule?.label, group?.tournamentKind), row])
      .filter(([round]) => Number.isFinite(round))
  )
  return {
    ...group,
    teams: group.teams.map(team => {
      const country = String(team?.name || '').trim()
      const teamId = Number(team?.teamId)
      const teamMatches = Array.isArray(team?.matches) ? team.matches : []
      return {
        ...team,
        matches: teamMatches.map(match => {
          const row = rowsByMatchRound.get(Number(match?.matchRound))
          if (!row) return match
          const source = teamId === Number(nationalTeamId)
            ? row
            : (row.countryValues && row.countryValues[country]) || null
          if (!source) return match
          const matchWithoutSimulationResult = { ...match }
          delete matchWithoutSimulationResult.simulationResult
          return {
            ...matchWithoutSimulationResult,
            attitude: normalizeAttitude(source.attitude),
            midfieldInput: String(source.midfieldInput ?? ''),
            formation: String(source.formation ?? ''),
            tactic: String(source.tactic ?? ''),
            hattrickTeamSpirit:
              source.hattrickTeamSpirit === null ||
              source.hattrickTeamSpirit === undefined ||
              source.hattrickTeamSpirit === ''
                ? null
                : Number(source.hattrickTeamSpirit),
            hattrickSpiritBeforeInput:
              source.hattrickSpiritBeforeInput === null ||
              source.hattrickSpiritBeforeInput === undefined ||
              source.hattrickSpiritBeforeInput === ''
                ? null
                : Number(source.hattrickSpiritBeforeInput),
            manualSpiritBeforeInput: String(source.manualSpiritBeforeInput ?? ''),
            midfieldAnchor: Boolean(source.midfieldAnchor),
          }
        }),
      }
    }),
  }
}
